import { Dispatch, RefObject, useCallback, useEffect, useRef } from 'react';

import { useDeleteKeepingSession } from '@/features/clips/api/delete-keeping-session';
import { useGetKeepingSessions } from '@/features/clips/api/get-keeping-sessions';
import { usePatchKeepingSession } from '@/features/clips/api/patch-keeping-session';
import { usePostKeepingSession } from '@/features/clips/api/post-keeping-session';
import { TClip } from '@/features/clips/types';
import { useProgramBroadcastFilters } from '@/stores/program-broadcast-filters';
import { useTimeKeepingStore } from '@/stores/time-keeping';
import { PresenceTypeEnum } from '@/types/presences';

import { UNDEFINED_SUPPORT_ID } from '../constants';
import {
  ItemLayoutType,
  TSessionLogo,
  TimeKeepingDashboardHandle,
  TimeKeepingSession,
  TimeKeepingSessionPostData,
} from '../types';
import { buildSessionItems } from '../utils/build-session-items';
import {
  clearStoredTimeKeepingSessionId,
  readStoredTimeKeepingSessionId,
  writeStoredTimeKeepingSessionId,
} from '../utils/session-storage';

import { LayoutAction } from './use-time-keeping-layout';

type RestoreSessionStateParams = {
  items: ItemLayoutType[];
  logos: string[];
  people: string[];
  sessionLogos: TSessionLogo[];
};

type UseTimeKeepingSessionParams = {
  items: ItemLayoutType[];
  selectedPeople: string[];
  dispatch: Dispatch<LayoutAction>;
  setSelectedLogos: (logos: string[]) => void;
  setSelectedPeople: (people: string[]) => void;
  setSessionSupports: (supports: string[]) => void;
  dashboardRef: RefObject<TimeKeepingDashboardHandle | null>;
  lastInitializedClipIdRef: React.MutableRefObject<string | undefined>;
  activeClip: TClip | null;
};

export function useTimeKeepingSession({
  items,
  selectedPeople,
  dispatch,
  setSelectedLogos,
  setSelectedPeople,
  setSessionSupports,
  dashboardRef,
  lastInitializedClipIdRef,
  activeClip,
}: UseTimeKeepingSessionParams) {
  const workspaces = useProgramBroadcastFilters(state => state.workspaces);
  const { data: savedSessions } = useGetKeepingSessions({ workspaces });
  const activeSessionId = useTimeKeepingStore(state => state.activeSessionId);
  const setActiveSessionId = useTimeKeepingStore(state => state.setActiveSessionId);
  const { mutateAsync: postSession } = usePostKeepingSession();
  const { mutateAsync: patchSession } = usePatchKeepingSession();
  const { mutate: deleteSession } = useDeleteKeepingSession();
  const sessionRestoredRef = useRef(false);

  useEffect(() => {
    if (activeSessionId) return;
    const stored = readStoredTimeKeepingSessionId();
    if (stored) setActiveSessionId(stored);
  }, []);

  const restoreSessionState = useCallback(
    ({ items: sessionItems, logos, people, sessionLogos }: RestoreSessionStateParams) => {
      dispatch({ type: 'RESTORE', items: sessionItems });
      setSelectedLogos(logos);
      setSelectedPeople(people);
      setSessionSupports(Array.from(new Set(sessionLogos.flatMap(l => l.supports))));
    },
    [dispatch, setSelectedLogos, setSelectedPeople, setSessionSupports]
  );

  // Restore persisted session when navigating back to this panel
  useEffect(() => {
    if (sessionRestoredRef.current) return;
    if (!activeSessionId || !Array.isArray(savedSessions) || savedSessions.length === 0) return;
    const session = savedSessions.find(s => s.id === activeSessionId);
    if (!session) return;
    sessionRestoredRef.current = true;
    if (activeClip) lastInitializedClipIdRef.current = activeClip.id;
    restoreSessionState({
      items: buildSessionItems({
        session,
        clipLogos: activeClip?.clip_logos ?? [],
        clipPeoples: activeClip?.clip_peoples ?? [],
      }),
      logos: session.logos.map(l => l.id),
      people: session.people,
      sessionLogos: session.logos,
    });
  }, [activeSessionId, savedSessions, restoreSessionState, activeClip, lastInitializedClipIdRef]);

  const saveSession = useCallback(
    async (sessionName: string) => {
      const liveLayout = dashboardRef.current?.getLayout() ?? [];
      const livePositions = new Map(liveLayout.map(l => [l.i, l]));

      const cleanItems = items.map(
        ({ presence_type, presence_id, x, y, w, h, min_w, max_w, min_h }) => {
          const live = livePositions.get(`${presence_type}:${presence_id}`);
          return {
            presence_type,
            presence_id,
            x: live?.x ?? x,
            y: live?.y ?? y,
            w: live?.w ?? w,
            h: live?.h ?? h,
            min_w,
            max_w,
            min_h,
          };
        }
      );

      const logoItems = items.filter(item => item.presence_type === PresenceTypeEnum.LOGOS);

      const sessionData: TimeKeepingSessionPostData = {
        name: sessionName,
        workspaces,
        logos: logoItems.map(item => ({
          id: item.presence_id,
          supports: item.supports
            .filter(s => s.presenceId !== UNDEFINED_SUPPORT_ID)
            .map(s => s.presenceId),
        })),
        people: selectedPeople,
        layout: cleanItems,
      };

      const activeSession = Array.isArray(savedSessions)
        ? savedSessions.find(s => s.id === activeSessionId)
        : undefined;
      const isSameSession = activeSession?.name === sessionName;

      try {
        if (activeSessionId && isSameSession) {
          await patchSession({ id: activeSessionId, ...sessionData });
        } else {
          const response = await postSession(sessionData);
          setActiveSessionId(response.id);
          writeStoredTimeKeepingSessionId(response.id);
        }
      } catch (error) {
        console.error(error);
      }
    },
    [
      items,
      selectedPeople,
      activeSessionId,
      savedSessions,
      workspaces,
      dashboardRef,
      postSession,
      patchSession,
      setActiveSessionId,
    ]
  );

  const loadSession = useCallback(
    (session: TimeKeepingSession) => {
      sessionRestoredRef.current = true;
      restoreSessionState({
        items: buildSessionItems({
          session,
          clipLogos: activeClip?.clip_logos ?? [],
          clipPeoples: activeClip?.clip_peoples ?? [],
        }),
        logos: session.logos.map(l => l.id),
        people: session.people,
        sessionLogos: session.logos,
      });
      setActiveSessionId(session.id);
      writeStoredTimeKeepingSessionId(session.id);
    },
    [restoreSessionState, setActiveSessionId, activeClip]
  );

  const clearSession = useCallback(() => {
    lastInitializedClipIdRef.current = undefined;
    setActiveSessionId(undefined);
    clearStoredTimeKeepingSessionId();
  }, [lastInitializedClipIdRef, setActiveSessionId]);

  const deleteActiveSession = useCallback(
    (sessionId: string) => {
      deleteSession({ id: sessionId });
    },
    [deleteSession]
  );

  return {
    sessions: Array.isArray(savedSessions) ? savedSessions : [],
    activeSessionId,
    saveSession,
    loadSession,
    clearSession,
    deleteActiveSession,
  };
}
